import { OvForm, OvFormInput, PageWrapper } from '@components'
import { Box, Button, Card, CardContent, Stack, Tooltip, Typography } from '@mui/material'
import { observer } from 'mobx-react-lite'
import { useState } from 'react'
import { useForm } from 'react-hook-form'
import { Link, useNavigate, useSearchParams } from 'react-router-dom'
import { toast } from 'react-toastify'
import { useStore } from '@hooks/useStore'
import { OvFormProvider } from 'src/components/core/ov-form-provider/ov-form-provider'
import { TOOLTIPS, URL_PATHS } from '@constants'

interface IResetPasswordForm {
  email: string
  password: string
  password_confirmation: string
}

export const ResetPasswordView = observer(() => {
  const { auth } = useStore()
  const navigate = useNavigate()
  const [searchParams] = useSearchParams()
  const [isSubmitting, setIsSubmitting] = useState(false)

  const token = searchParams.get('token')
  const emailParam = searchParams.get('email')

  const form = useForm<IResetPasswordForm>({
    defaultValues: {
      email: emailParam || '',
      password: '',
      password_confirmation: '',
    },
  })

  const onSubmit = async (data: IResetPasswordForm) => {
    if (!token) {
      toast.error('Ongeldige reset link')
      return
    }

    setIsSubmitting(true)
    try {
      await auth.resetPassword({
        ...data,
        token,
      })
      toast.success('Je wachtwoord is gereset.')
      navigate(URL_PATHS.LOGIN)
    } catch (error: any) {
      toast.error(error.response?.data?.message || 'Er is iets misgegaan. Probeer het opnieuw.')
    } finally {
      setIsSubmitting(false)
    }
  }

  const handleSubmit = () => {
    form.handleSubmit(onSubmit, errors => {
      console.log('Validation errors:', errors)
      toast.error('Controleer de invoervelden')
    })()
  }

  if (!token) {
    return (
      <PageWrapper background="primary-gradient">
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            flex: '1 0 auto',
          }}>
          <Card sx={{ maxWidth: 450, width: '100%' }}>
            <CardContent sx={{ p: 4 }}>
              <Stack spacing={3}>
                <Typography
                  variant="h4"
                  align="center"
                  color="error">
                  Ongeldige Link
                </Typography>
                <Typography
                  variant="body1"
                  align="center">
                  Deze wachtwoord reset link is ongeldig of verlopen. Vraag een nieuwe reset link aan.
                </Typography>
                <Tooltip title={TOOLTIPS.REQUEST_NEW_RESET_LINK}>
                  <Button
                    component={Link}
                    to={URL_PATHS.FORGOT_PASSWORD}
                    variant="contained"
                    fullWidth>
                    Nieuwe Link Aanvragen
                  </Button>
                </Tooltip>
              </Stack>
            </CardContent>
          </Card>
        </Box>
      </PageWrapper>
    )
  }

  return (
    <PageWrapper background="primary-gradient">
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          flex: '1 0 auto',
        }}>
        <Card sx={{ maxWidth: 450, width: '100%' }}>
          <CardContent sx={{ p: 4 }}>
            <OvFormProvider
              form={form}
              onSubmit={handleSubmit}
              loading={isSubmitting}
              enableNavigationBlocker={false}>
              <OvForm>
                <Stack spacing={3}>
                  <Typography
                    variant="h4"
                    align="center">
                    Reset Wachtwoord
                  </Typography>
                  <Typography
                    variant="body2"
                    align="center"
                    color="text.secondary">
                    Vul je nieuwe wachtwoord in
                  </Typography>

                  <OvFormInput<IResetPasswordForm>
                    name="email"
                    label="E-mailadres"
                    type="email"
                    readOnly
                    rules={{
                      required: 'E-mailadres is verplicht',
                    }}
                  />

                  <OvFormInput<IResetPasswordForm>
                    name="password"
                    label="Nieuw Wachtwoord"
                    type="password"
                    rules={{
                      required: 'Wachtwoord is verplicht',
                      minLength: {
                        value: 8,
                        message: 'Wachtwoord moet minimaal 8 karakters zijn',
                      },
                    }}
                  />

                  <OvFormInput<IResetPasswordForm>
                    name="password_confirmation"
                    label="Bevestig Wachtwoord"
                    type="password"
                    rules={{
                      required: 'Bevestig je wachtwoord',
                      validate: value => value === form.getValues('password') || 'Wachtwoorden komen niet overeen',
                    }}
                  />

                  <Tooltip title={TOOLTIPS.RESET_PASSWORD_BUTTON}>
                    <span>
                      <Button
                        type="submit"
                        variant="contained"
                        fullWidth
                        disabled={isSubmitting}>
                        {isSubmitting ? 'Resetten...' : 'Wachtwoord Resetten'}
                      </Button>
                    </span>
                  </Tooltip>

                  <Box sx={{ textAlign: 'center' }}>
                    <Typography variant="body2">
                      <Link
                        to={URL_PATHS.LOGIN}
                        style={{ textDecoration: 'none', color: 'primary.main' }}>
                        Terug naar inloggen
                      </Link>
                    </Typography>
                  </Box>
                </Stack>
              </OvForm>
            </OvFormProvider>
          </CardContent>
        </Card>
      </Box>
    </PageWrapper>
  )
})
